// Port of Location/FixBroadcaster.swift: multicasts values to any number of subscribers.
//
// Two ways to subscribe:
// - `subscribe(listener)`: a callback invoked synchronously with every value (the UI's usual path);
// - `makeStream()`: an async iterator buffering only the newest value (Swift `.bufferingNewest(1)`), so a slow
//   consumer sees the latest fix instead of a backlog. Breaking out of `for await` (or calling `return()`) removes it.
// `finishAll()` ends every stream and removes every subscriber (Swift: on deallocation).

export type Unsubscribe = () => void;

/** An async iterator over the newest values only. */
export class FixStream<T> implements AsyncIterableIterator<T> {
  private buffered: { value: T } | undefined;
  private waiting: ((result: IteratorResult<T>) => void) | undefined;
  private finished = false;

  constructor(private readonly onTermination: () => void) {}

  /** Delivers a value (replacing an unread one). */
  push(value: T): void {
    if (this.finished) return;
    if (this.waiting) {
      const resolve = this.waiting;
      this.waiting = undefined;
      resolve({ value, done: false });
    } else {
      this.buffered = { value };
    }
  }

  /** Ends the stream; a buffered value is still delivered first. */
  finish(): void {
    if (this.finished) return;
    this.finished = true;
    if (this.waiting) {
      const resolve = this.waiting;
      this.waiting = undefined;
      resolve({ value: undefined, done: true });
    }
    this.onTermination();
  }

  next(): Promise<IteratorResult<T>> {
    if (this.buffered) {
      const { value } = this.buffered;
      this.buffered = undefined;
      return Promise.resolve({ value, done: false });
    }
    if (this.finished) return Promise.resolve({ value: undefined, done: true });
    return new Promise((resolve) => (this.waiting = resolve));
  }

  return(): Promise<IteratorResult<T>> {
    this.buffered = undefined;
    this.finish();
    return Promise.resolve({ value: undefined, done: true });
  }

  [Symbol.asyncIterator](): FixStream<T> {
    return this;
  }
}

export class FixBroadcaster<T> {
  private readonly listeners = new Set<(value: T) => void>();
  private readonly streams = new Set<FixStream<T>>();

  /** Calls `listener` with every value (and with `initial` right away when given). */
  subscribe(listener: (value: T) => void, initial?: T): Unsubscribe {
    this.listeners.add(listener);
    if (initial !== undefined) listener(initial);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** A new subscriber stream, optionally delivering `initial` immediately. */
  makeStream(initial?: T): FixStream<T> {
    const stream: FixStream<T> = new FixStream<T>(() => this.streams.delete(stream));
    this.streams.add(stream);
    if (initial !== undefined) stream.push(initial);
    return stream;
  }

  /** Delivers `value` to every subscriber. */
  yield(value: T): void {
    for (const stream of [...this.streams]) stream.push(value);
    for (const listener of [...this.listeners]) {
      try {
        listener(value);
      } catch (error) {
        console.error('Fix listener failed', error);
      }
    }
  }

  /** Ends every stream and removes every subscriber. */
  finishAll(): void {
    const streams = [...this.streams];
    this.streams.clear();
    this.listeners.clear();
    for (const stream of streams) stream.finish();
  }

  /** Number of active subscribers (streams and callbacks). */
  get subscriberCount(): number {
    return this.streams.size + this.listeners.size;
  }
}
